import { supabase } from './supabase';
import { getStripePriceId, formatAmountForStripe } from './stripe';

export interface CreateCheckoutSessionParams {
  planId: string;
  userId: string;
  planName: string;
  billingCycle: 'monthly' | 'annual';
  price: number;
}

export interface CreatePortalSessionParams {
  customerId: string;
  returnUrl: string;
}

export interface StripeApiResponse {
  success: boolean;
  data?: any;
  error?: string;
}

export const stripeApi = {
  // Create a Stripe checkout session
  async createCheckoutSession(params: CreateCheckoutSessionParams): Promise<StripeApiResponse> {
    try {
      console.log('Creating checkout session with params:', params);
      
      const stripePriceId = getStripePriceId(params.planName, params.billingCycle);
      console.log('Stripe price ID:', stripePriceId);
      
      if (!stripePriceId) {
        throw new Error('Stripe price ID not found for this plan');
      }

      // Call Supabase Edge Function to create checkout session
      console.log('Calling Supabase Edge Function...');
      
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: {
          planId: params.planId,
          userId: params.userId,
          planName: params.planName,
          billingCycle: params.billingCycle,
          price: params.price,
          stripePriceId,
        },
      });

      console.log('Edge Function response:', { data, error });

      if (error) {
        console.error('Edge Function error details:', {
          message: error.message,
          status: error.status,
          statusText: error.statusText,
          details: error
        });
        throw error;
      }

      console.log('Checkout session created successfully:', data);
      return {
        success: true,
        data,
      };
    } catch (error: any) {
      console.error('Error in createCheckoutSession:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  },

  // Create a Stripe customer portal session
  async createPortalSession(params: CreatePortalSessionParams): Promise<StripeApiResponse> {
    try {
      const { data, error } = await supabase.functions.invoke('create-portal-session', {
        body: {
          customerId: params.customerId,
          returnUrl: params.returnUrl,
        },
      });

      if (error) {
        throw error;
      }

      return {
        success: true,
        data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message,
      };
    }
  },

  // Verify webhook signature (for webhook handling)
  async verifyWebhookSignature(payload: string, signature: string): Promise<StripeApiResponse> {
    try {
      const { data, error } = await supabase.functions.invoke('verify-webhook', {
        body: {
          payload,
          signature,
        },
      });

      if (error) {
        throw error;
      }

      return {
        success: true,
        data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message,
      };
    }
  },

  // Get customer details
  async getCustomer(customerId: string): Promise<StripeApiResponse> {
    try {
      const { data, error } = await supabase.functions.invoke('get-customer', {
        body: { customerId },
      });

      if (error) {
        throw error;
      }

      return {
        success: true,
        data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message,
      };
    }
  },

  // Get subscription details
  async getSubscription(subscriptionId: string): Promise<StripeApiResponse> {
    try {
      const { data, error } = await supabase.functions.invoke('get-subscription', {
        body: { subscriptionId },
      });

      if (error) {
        throw error;
      }

      return {
        success: true,
        data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message,
      };
    }
  },
}; 